// ================================================================
// PROPRIETARY & CONFIDENTIAL — Miskulin Technologies Inc.
// Forge Casino — Player Vault
// $FORGE balance management, deposits, withdrawals
// ================================================================
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useCasino } from '../context/CasinoShell';

const QUICK_AMOUNTS = [10, 25, 50, 100, 250, 500];

export default function PlayerVault() {
  const navigate = useNavigate();
  const { forgeBalance, deposit, withdraw, kycStatus, sessionStats, transactionHistory } = useCasino();
  const [mode, setMode] = useState('deposit'); // deposit | withdraw
  const [amount, setAmount] = useState('');
  const [message, setMessage] = useState(null);

  const handleAction = () => {
    const val = parseFloat(amount);
    if (isNaN(val) || val <= 0) {
      setMessage({ type: 'error', text: 'Enter a valid amount' });
      return;
    }

    let result;
    if (mode === 'deposit') {
      result = deposit(val);
    } else {
      result = withdraw(val);
    }

    if (result.success) {
      setMessage({ type: 'success', text: `${mode === 'deposit' ? 'Deposited' : 'Withdrawn'} $${val.toFixed(2)} $FORGE` });
      setAmount('');
    } else {
      setMessage({ type: 'error', text: result.reason });
    }
  };

  const recentTx = transactionHistory.slice(-10).reverse();

  return (
    <div style={styles.container}>
      <h1 style={styles.title}>💰 PLAYER VAULT</h1>

      {/* Balance Card */}
      <div style={styles.balanceCard}>
        <span style={styles.balanceLabel}>$FORGE BALANCE</span>
        <span style={styles.balanceValue}>${forgeBalance.toFixed(2)}</span>
        <div style={styles.balanceStats}>
          <span style={styles.miniStat}>Bets: ${sessionStats.totalBets.toFixed(2)}</span>
          <span style={styles.miniStat}>Wins: ${sessionStats.totalWins.toFixed(2)}</span>
          <span style={{
            ...styles.miniStat,
            color: sessionStats.netPosition >= 0 ? '#00FF88' : '#DC2626',
          }}>
            Net: {sessionStats.netPosition >= 0 ? '+' : ''}${sessionStats.netPosition.toFixed(2)}
          </span>
        </div>
      </div>

      {/* Deposit / Withdraw */}
      <div style={styles.actionCard}>
        <div style={styles.modeSwitch}>
          <button
            style={{ ...styles.modeBtn, background: mode === 'deposit' ? '#DC2626' : '#1a1a1a', color: mode === 'deposit' ? '#fff' : '#888' }}
            onClick={() => { setMode('deposit'); setMessage(null); }}
          >DEPOSIT</button>
          <button
            style={{ ...styles.modeBtn, background: mode === 'withdraw' ? '#00FF88' : '#1a1a1a', color: mode === 'withdraw' ? '#000' : '#888' }}
            onClick={() => { setMode('withdraw'); setMessage(null); }}
          >WITHDRAW</button>
        </div>

        {mode === 'withdraw' && kycStatus !== 'verified' && (
          <div style={styles.kycWarning}>
            🛡️ KYC verification required for withdrawals.
            <button style={styles.kycBtn} onClick={() => navigate('/kyc')}>Verify Identity</button>
          </div>
        )}

        {/* Quick amounts */}
        <div style={styles.quickAmounts}>
          {QUICK_AMOUNTS.map(amt => (
            <button
              key={amt}
              style={styles.quickBtn}
              onClick={() => setAmount(String(amt))}
            >${amt}</button>
          ))}
        </div>

        {/* Amount input */}
        <div style={styles.inputRow}>
          <span style={styles.inputPrefix}>$</span>
          <input
            style={styles.input}
            type="number"
            value={amount}
            onChange={e => setAmount(e.target.value)}
            placeholder="Enter amount"
            min="0"
            step="0.01"
          />
          <button
            style={styles.actionBtn}
            onClick={handleAction}
            disabled={mode === 'withdraw' && kycStatus !== 'verified'}
          >
            {mode === 'deposit' ? 'DEPOSIT' : 'WITHDRAW'}
          </button>
        </div>

        {/* Message */}
        {message && (
          <div style={{
            ...styles.message,
            background: message.type === 'success' ? '#00FF8822' : '#DC262622',
            borderColor: message.type === 'success' ? '#00FF8866' : '#DC262666',
            color: message.type === 'success' ? '#00FF88' : '#DC2626',
          }}>
            {message.text}
          </div>
        )}
      </div>

      {/* Navigation */}
      <div style={styles.navButtons}>
        <button style={styles.navBtn} onClick={() => navigate('/vault/history')}>
          📋 Transaction History
        </button>
        <button style={styles.navBtn} onClick={() => navigate('/vault/verify')}>
          ✅ Verify Fairness
        </button>
      </div>

      {/* Recent Transactions */}
      {recentTx.length > 0 && (
        <div style={styles.recentSection}>
          <h3 style={styles.recentTitle}>RECENT TRANSACTIONS</h3>
          {recentTx.map((tx, i) => (
            <div key={i} style={styles.txRow}>
              <div style={styles.txLeft}>
                <span style={styles.txIcon}>
                  {tx.type === 'deposit' ? '📥' : tx.type === 'withdraw' ? '📤' : tx.type === 'win' ? '🏆' : '🎰'}
                </span>
                <div>
                  <span style={styles.txDesc}>{tx.description}</span>
                  <span style={styles.txTime}>{new Date(tx.timestamp).toLocaleString()}</span>
                </div>
              </div>
              <span style={{
                ...styles.txAmount,
                color: tx.type === 'deposit' || tx.type === 'win' ? '#00FF88' : '#DC2626',
              }}>
                {tx.type === 'deposit' || tx.type === 'win' ? '+' : '-'}${tx.amount.toFixed(2)}
              </span>
            </div>
          ))}
        </div>
      )}

      {/* Info */}
      <div style={styles.info}>
        <p style={styles.infoText}>
          🔒 All transactions are recorded on-chain for full transparency.
          $FORGE credits can be used across the entire Forge ecosystem.
        </p>
      </div>
    </div>
  );
}

const styles = {
  container: { padding: 16, maxWidth: 560, margin: '0 auto', minHeight: '100vh', background: '#0a0a0a' },
  title: { fontSize: 20, fontWeight: 900, color: '#fff', letterSpacing: 2, marginBottom: 16 },
  balanceCard: {
    padding: 20, borderRadius: 12, background: 'linear-gradient(135deg, #1a0000 0%, #111 100%)',
    border: '1px solid #DC262644', textAlign: 'center', marginBottom: 16,
  },
  balanceLabel: { display: 'block', fontSize: 10, color: '#DC2626', fontWeight: 700, letterSpacing: 2 },
  balanceValue: { display: 'block', fontSize: 40, fontWeight: 900, color: '#FFD700', fontFamily: "'Courier New', monospace", margin: '8px 0' },
  balanceStats: { display: 'flex', justifyContent: 'center', gap: 16 },
  miniStat: { fontSize: 10, color: '#888' },
  actionCard: { padding: 16, background: '#111', borderRadius: 12, border: '1px solid #222', marginBottom: 16 },
  modeSwitch: { display: 'flex', gap: 4, marginBottom: 16 },
  modeBtn: { flex: 1, padding: '10px', borderRadius: 8, border: 'none', fontSize: 12, fontWeight: 900, letterSpacing: 2, cursor: 'pointer' },
  kycWarning: {
    padding: 12, background: '#FFD70022', border: '1px solid #FFD70044', borderRadius: 8,
    fontSize: 11, color: '#FFD700', marginBottom: 12, textAlign: 'center',
  },
  kycBtn: {
    display: 'block', margin: '8px auto 0', padding: '6px 16px', borderRadius: 6,
    border: '1px solid #FFD700', background: '#FFD70033', color: '#FFD700',
    fontSize: 10, fontWeight: 700, cursor: 'pointer',
  },
  quickAmounts: { display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 6, marginBottom: 12 },
  quickBtn: {
    padding: 8, borderRadius: 6, border: '1px solid #333', background: '#0a0a0a',
    color: '#fff', fontSize: 12, fontWeight: 700, cursor: 'pointer',
  },
  inputRow: { display: 'flex', gap: 8, alignItems: 'center' },
  inputPrefix: { fontSize: 20, color: '#FFD700', fontWeight: 900 },
  input: {
    flex: 1, padding: '10px 12px', borderRadius: 8, border: '1px solid #333',
    background: '#0a0a0a', color: '#fff', fontSize: 16, fontWeight: 700,
  },
  actionBtn: {
    padding: '10px 20px', borderRadius: 8, border: '2px solid #DC2626',
    background: 'linear-gradient(180deg, #DC2626 0%, #991B1B 100%)',
    color: '#fff', fontSize: 12, fontWeight: 900, letterSpacing: 1, cursor: 'pointer',
  },
  message: { padding: 10, borderRadius: 8, border: '1px solid', fontSize: 12, textAlign: 'center', marginTop: 12 },
  navButtons: { display: 'flex', gap: 8, marginBottom: 16 },
  navBtn: {
    flex: 1, padding: 10, borderRadius: 8, border: '1px solid #222', background: '#111',
    color: '#aaa', fontSize: 11, fontWeight: 700, cursor: 'pointer', textAlign: 'center',
  },
  recentSection: { background: '#111', borderRadius: 12, border: '1px solid #222', padding: 16, marginBottom: 16 },
  recentTitle: { fontSize: 12, color: '#666', fontWeight: 700, letterSpacing: 1, marginBottom: 12 },
  txRow: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 0', borderBottom: '1px solid #1a1a1a' },
  txLeft: { display: 'flex', gap: 8, alignItems: 'center' },
  txIcon: { fontSize: 16 },
  txDesc: { display: 'block', fontSize: 11, color: '#ccc' },
  txTime: { display: 'block', fontSize: 9, color: '#555' },
  txAmount: { fontSize: 13, fontWeight: 900, fontFamily: "'Courier New', monospace" },
  info: { textAlign: 'center', padding: 16 },
  infoText: { fontSize: 10, color: '#444', lineHeight: 1.6 },
};
